/**
 * Veloura Maison - theme.js
 * Vanilla JS, no dependencies. Cart uses the Shopify AJAX API with the
 * Section Rendering API so all markup (and money formatting) stays in Liquid.
 */
(function () {
  'use strict';

  var theme = window.theme || {};
  var routes = theme.routes || {};
  var strings = theme.strings || {};
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Utilities
     --------------------------------------------------------------------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  var toastTimer;
  function toast(message) {
    var el = document.getElementById('Toast');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('is-visible'); }, 2800);
  }

  var lockCount = 0;
  function lockScroll(on) {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    document.body.classList.toggle('is-locked', lockCount > 0);
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function trapFocus(container, event) {
    var nodes = $$(FOCUSABLE, container).filter(function (n) { return n.offsetParent !== null; });
    if (!nodes.length) return;
    var first = nodes[0], last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  /* ---------------------------------------------------------------------
     Modal layer (drawers, search, popup) - one open at a time
     --------------------------------------------------------------------- */
  var active = null; // { el, opener, onClose }

  function openLayer(el, opener, openClass) {
    if (!el) return;
    if (active && active.el === el) return;
    if (active) closeLayer(true);
    el.hidden = false;
    el.getBoundingClientRect(); // force reflow so the transition runs
    el.classList.add(openClass || 'is-open');
    active = { el: el, opener: opener || document.activeElement, cls: openClass || 'is-open' };
    lockScroll(true);
    if (el.id) $$('[aria-controls="' + el.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    var panel = el.querySelector('[tabindex="-1"]') || el;
    setTimeout(function () {
      var target = el.querySelector('[data-autofocus]') || panel;
      target.focus({ preventScroll: true });
    }, 60);
  }

  function closeLayer(skipFocus) {
    if (!active) return;
    var layer = active;
    active = null;
    layer.el.classList.remove(layer.cls);
    lockScroll(false);
    if (layer.el.id) $$('[aria-controls="' + layer.el.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    setTimeout(function () { if (!layer.el.classList.contains(layer.cls)) layer.el.hidden = true; }, 500);
    if (layer.el.hasAttribute('data-popup')) rememberPopup();
    if (!skipFocus && layer.opener && typeof layer.opener.focus === 'function') layer.opener.focus({ preventScroll: true });
  }

  document.addEventListener('keydown', function (e) {
    if (!active) return;
    if (e.key === 'Escape') { closeLayer(); return; }
    if (e.key === 'Tab') trapFocus(active.el, e);
  });

  /* ---------------------------------------------------------------------
     Global click delegation
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var t = e.target;

    var opener = t.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openLayer(document.getElementById(opener.getAttribute('data-drawer-open')), opener);
      return;
    }
    if (t.closest('[data-drawer-close]') && active) { e.preventDefault(); closeLayer(); return; }

    var cartOpen = t.closest('[data-cart-open]');
    if (cartOpen && theme.cartType === 'drawer' && $('[data-cart-drawer]')) {
      e.preventDefault();
      openLayer($('[data-cart-drawer]'), cartOpen);
      return;
    }

    if (t.closest('[data-search-open]')) { e.preventDefault(); openSearch(t.closest('[data-search-open]')); return; }
    if (t.closest('[data-search-close]')) { e.preventDefault(); closeLayer(); return; }
    if (t.closest('[data-popup-close]')) { e.preventDefault(); closeLayer(); return; }

    var change = t.closest('[data-cart-change]');
    if (change) {
      e.preventDefault();
      changeLine(change.getAttribute('data-cart-change'), parseInt(change.getAttribute('data-quantity'), 10), change);
      return;
    }

    var quick = t.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      quick.classList.add('is-loading');
      addToCart({ items: [{ id: parseInt(quick.getAttribute('data-quick-add'), 10), quantity: 1 }] })
        .finally(function () { quick.classList.remove('is-loading'); });
      return;
    }

    var copy = t.closest('[data-copy-link]');
    if (copy) {
      e.preventDefault();
      var url = copy.getAttribute('data-copy-link');
      if (navigator.clipboard) navigator.clipboard.writeText(url).then(function () { toast('Link copied'); });
      return;
    }

    var accountToggle = t.closest('[data-account-toggle]');
    if (accountToggle) {
      e.preventDefault();
      $$('[data-account-view]').forEach(function (v) { v.hidden = v.id !== accountToggle.getAttribute('data-account-toggle'); });
    }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form.hasAttribute('data-confirm') && !window.confirm(form.getAttribute('data-confirm'))) e.preventDefault();
  });

  /* ---------------------------------------------------------------------
     Header: sticky behaviour, transparent overlay, mega menu
     --------------------------------------------------------------------- */
  function initHeader() {
    var wrapper = $('.section-header');
    var header = $('[data-header]');
    if (!wrapper || !header) return;
    var mode = header.getAttribute('data-sticky');
    if (mode === 'none') wrapper.classList.add('is-static');

    function setHeight() {
      document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
    }
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);

    var overlay = header.classList.contains('header--overlay');
    var lastY = window.scrollY;
    var ticking = false;
    function onScroll() {
      var y = window.scrollY;
      if (overlay) header.classList.toggle('is-solid', y > 40 || header.matches(':hover') || header.matches(':focus-within'));
      if (mode === 'scroll-up') {
        var hide = y > lastY && y > header.offsetHeight + 80 && !active;
        wrapper.classList.toggle('is-hidden', hide);
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
    onScroll();

    if (overlay) {
      header.addEventListener('mouseenter', function () { header.classList.add('is-solid'); });
      header.addEventListener('mouseleave', function () { if (window.scrollY <= 40 && !header.contains(document.activeElement)) header.classList.remove('is-solid'); });
      header.addEventListener('focusin', function () { header.classList.add('is-solid'); });
    }

    /* Mega menu: opens on click (not hover), one at a time */
    var megaItems = $$('.nav__item.has-mega', header);
    function closeMegas(except) {
      megaItems.forEach(function (it) {
        if (it === except) return;
        it.classList.remove('is-open');
        it.querySelector('.nav__link').setAttribute('aria-expanded', 'false');
      });
    }
    megaItems.forEach(function (item) {
      var link = item.querySelector('.nav__link');
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var open = !item.classList.contains('is-open');
        closeMegas(item);
        item.classList.toggle('is-open', open);
        link.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (overlay) header.classList.add('is-solid');
      });
      link.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          closeMegas(item);
          item.classList.add('is-open');
          link.setAttribute('aria-expanded', 'true');
          var first = item.querySelector('.mega a');
          if (first) first.focus();
        }
      });
      item.addEventListener('focusout', function (e) {
        if (e.relatedTarget && !item.contains(e.relatedTarget)) { item.classList.remove('is-open'); link.setAttribute('aria-expanded', 'false'); }
      });
    });
    if (!header._megaBound) {
      header._megaBound = true;
      document.addEventListener('click', function (e) {
        if (!e.target.closest('.nav__item.has-mega')) closeMegas(null);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && megaItems.some(function (it) { return it.classList.contains('is-open'); })) {
          var openItem = megaItems.filter(function (it) { return it.classList.contains('is-open'); })[0];
          closeMegas(null);
          openItem.querySelector('.nav__link').focus();
        }
      });
    }
  }

  /* ---------------------------------------------------------------------
     Announcement bar
     --------------------------------------------------------------------- */
  function initAnnouncement(root) {
    $$('[data-rotate]', root).forEach(function (wrap) {
      var slides = $$('.announcement__slide', wrap);
      if (slides.length < 2 || reducedMotion) return;
      var i = 0;
      var speed = (parseInt(wrap.getAttribute('data-rotate'), 10) || 5) * 1000;
      if (wrap._timer) clearInterval(wrap._timer);
      wrap._timer = setInterval(function () {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, speed);
    });
  }

  /* ---------------------------------------------------------------------
     Hero slideshow
     --------------------------------------------------------------------- */
  function initSlideshow(root) {
    $$('[data-slideshow]', root).forEach(function (show) {
      var slides = $$('[data-slide]', show);
      var dots = $$('[data-slide-to]', show);
      if (slides.length < 2) return;
      var index = 0, timer = null;
      var speed = parseInt(show.getAttribute('data-speed'), 10) || 7000;
      var autoplay = show.getAttribute('data-autoplay') === 'true' && !reducedMotion;
      show.style.setProperty('--speed', speed + 'ms');

      function go(n) {
        index = (n + slides.length) % slides.length;
        slides.forEach(function (s, i) {
          var on = i === index;
          s.classList.toggle('is-active', on);
          s.setAttribute('aria-hidden', on ? 'false' : 'true');
          $$('a, button', s).forEach(function (el) { if (on) el.removeAttribute('tabindex'); else el.setAttribute('tabindex', '-1'); });
        });
        dots.forEach(function (d, i) {
          d.classList.remove('is-active');
          if (i === index) { d.offsetWidth; d.classList.add('is-active'); }
          d.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
      }
      function play() { if (!autoplay) { show.classList.add('is-paused'); return; } stop(); show.classList.remove('is-paused'); timer = setInterval(function () { go(index + 1); }, speed); }
      function stop() { clearInterval(timer); timer = null; }

      dots.forEach(function (d) { d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-slide-to'), 10)); play(); }); });
      var prev = $('[data-slide-prev]', show), next = $('[data-slide-next]', show);
      if (prev) prev.addEventListener('click', function () { go(index - 1); play(); });
      if (next) next.addEventListener('click', function () { go(index + 1); play(); });
      show.addEventListener('mouseenter', function () { stop(); show.classList.add('is-paused'); });
      show.addEventListener('mouseleave', play);
      show.addEventListener('focusin', function () { stop(); show.classList.add('is-paused'); });

      var startX = null;
      show.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      show.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) { go(index + (dx < 0 ? 1 : -1)); play(); }
        startX = null;
      });

      show._go = go;
      show._stop = stop;
      go(0);
      play();
    });
  }

  /* ---------------------------------------------------------------------
     Horizontal sliders (product carousels)
     --------------------------------------------------------------------- */
  function initSliders(root) {
    $$('[data-slider]', root).forEach(function (track) {
      var section = track.closest('.shopify-section') || track.parentElement;
      var prev = $('[data-slider-prev]', section);
      var next = $('[data-slider-next]', section);
      if (!prev || !next) return;
      function step() {
        var item = track.firstElementChild;
        if (!item) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var per = Math.max(1, Math.round(track.clientWidth / (item.offsetWidth + gap)));
        return per * (item.offsetWidth + gap);
      }
      function update() {
        prev.disabled = track.scrollLeft <= 4;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      }
      prev.onclick = function () { track.scrollBy({ left: -step(), behavior: reducedMotion ? 'auto' : 'smooth' }); };
      next.onclick = function () { track.scrollBy({ left: step(), behavior: reducedMotion ? 'auto' : 'smooth' }); };
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }

  /* ---------------------------------------------------------------------
     Testimonials
     --------------------------------------------------------------------- */
  function initQuotes(root) {
    $$('[data-quotes]', root).forEach(function (slider) {
      var section = slider.closest('.shopify-section') || slider.parentElement;
      var quotes = $$('[data-quote]', slider);
      if (quotes.length < 2) return;
      var i = 0, label = $('[data-quote-index]', section), timer;
      function go(n) {
        i = (n + quotes.length) % quotes.length;
        quotes.forEach(function (q, k) { q.classList.toggle('is-active', k === i); q.setAttribute('aria-hidden', k === i ? 'false' : 'true'); });
        if (label) label.textContent = i + 1;
      }
      function auto() { clearInterval(timer); timer = setInterval(function () { go(i + 1); }, 7000); }
      var prev = $('[data-quote-prev]', section), next = $('[data-quote-next]', section);
      if (prev) prev.onclick = function () { go(i - 1); auto(); };
      if (next) next.onclick = function () { go(i + 1); auto(); };
      slider.addEventListener('mouseenter', function () { clearInterval(timer); });
      slider.addEventListener('mouseleave', auto);
      auto();
    });
  }

  /* ---------------------------------------------------------------------
     Reveal on scroll
     --------------------------------------------------------------------- */
  var revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }) : null;

  function initReveal(root) {
    $$('.reveal:not(.is-visible)', root).forEach(function (el, i) {
      if (!revealObserver) { el.classList.add('is-visible'); return; }
      el.style.transitionDelay = Math.min(i % 6, 5) * 70 + 'ms';
      revealObserver.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
     Cart
     --------------------------------------------------------------------- */
  function cartSections() {
    var list = [];
    if ($('[data-cart-drawer]')) list.push('cart-drawer');
    var page = $('[data-cart-page]');
    if (page) list.push(page.getAttribute('data-section-id'));
    return list;
  }

  function updateCount(count) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      el.classList.add('is-bumped');
      setTimeout(function () { el.classList.remove('is-bumped'); }, 300);
    });
  }

  function renderSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (!html) return;
      var doc = parseHTML(html);
      if (id === 'cart-drawer') {
        var fresh = $('[data-cart-drawer-inner]', doc);
        var current = $('[data-cart-drawer-inner]');
        if (fresh && current) {
          current.replaceWith(fresh);
          updateCount(parseInt(fresh.getAttribute('data-cart-count-value'), 10) || 0);
        }
      } else {
        var freshPage = $('[data-cart-page-inner]', doc);
        var currentPage = $('[data-cart-page-inner]');
        if (freshPage && currentPage) currentPage.replaceWith(freshPage);
      }
    });
  }

  function refreshCount() {
    return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCount(cart.item_count); return cart; });
  }

  function addToCart(body, errorEl) {
    var sections = cartSections();
    var isForm = body instanceof FormData;
    if (sections.length) {
      if (isForm) { body.append('sections', sections.join(',')); body.append('sections_url', window.location.pathname); }
      else { body.sections = sections.join(','); body.sections_url = window.location.pathname; }
    }
    if (errorEl) errorEl.hidden = true;
    return fetch(routes.cartAdd + '.js', {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok || res.data.status) {
          var msg = res.data.description || res.data.message || strings.cartError;
          if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; }
          toast(msg);
          return;
        }
        document.dispatchEvent(new CustomEvent('cart:added', { detail: res.data }));
        if (res.data.sections) renderSections(res.data.sections);
        var drawer = $('[data-cart-drawer]');
        if (theme.cartType === 'drawer' && drawer) {
          openLayer(drawer);
        } else {
          refreshCount();
          toast(strings.added);
        }
      })
      .catch(function () { toast(strings.cartError); });
  }

  function changeLine(key, quantity, trigger) {
    var item = trigger ? trigger.closest('[data-cart-item]') : null;
    if (item) item.classList.add('is-updating');
    var drawer = $('[data-cart-drawer]');
    if (drawer) drawer.classList.add('is-loading');
    return fetch(routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity), sections: cartSections().join(','), sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok || res.data.status) { toast(res.data.description || strings.cartError); if (item) item.classList.remove('is-updating'); return; }
        renderSections(res.data.sections);
        updateCount(res.data.item_count);
      })
      .catch(function () { toast(strings.cartError); if (item) item.classList.remove('is-updating'); })
      .finally(function () { if (drawer) drawer.classList.remove('is-loading'); });
  }

  /* ---------------------------------------------------------------------
     Product page
     --------------------------------------------------------------------- */
  function initProduct(root) {
    $$('[data-product-section]', root).forEach(function (section) {
      var jsonEl = $('[data-product-json]', section);
      if (!jsonEl) return;
      var product;
      try { product = JSON.parse(jsonEl.textContent); } catch (err) { return; }
      var sectionId = section.getAttribute('data-section-id');
      var form = $('[data-product-form]', section);
      var idInput = form ? $('[data-variant-input]', form) : null;
      var addBtn = form ? $('[data-add-button]', form) : null;
      var addLabel = addBtn ? $('[data-add-label]', addBtn) : null;
      var stickyBtn = $('[data-sticky-add]', section);
      var errorEl = form ? $('[data-form-error]', form) : null;
      var inputs = $$('[data-option-index]', section);

      /* Quantity */
      $$('[data-qty]', section).forEach(function (q) {
        var input = $('input', q);
        q.addEventListener('click', function (e) {
          var v = parseInt(input.value, 10) || 1;
          if (e.target.closest('[data-qty-minus]')) input.value = Math.max(1, v - 1);
          if (e.target.closest('[data-qty-plus]')) input.value = Math.min(99, v + 1);
        });
      });

      /* Variant selection */
      function selectedOptions() {
        var opts = [];
        inputs.forEach(function (el) {
          var idx = parseInt(el.getAttribute('data-option-index'), 10);
          if (el.tagName === 'SELECT' || el.checked) opts[idx] = el.value;
        });
        return opts;
      }

      function markAvailability(opts) {
        inputs.forEach(function (el) {
          if (el.tagName !== 'INPUT') return;
          var idx = parseInt(el.getAttribute('data-option-index'), 10);
          var available = product.variants.some(function (v) {
            return v.available && v.options[idx] === el.value && v.options.every(function (o, k) { return k === idx || o === opts[k]; });
          });
          el.classList.toggle('is-unavailable', !available);
        });
      }

      function setButton(variant) {
        var available = variant && variant.available;
        var label = !variant ? strings.unavailable : available ? strings.addToBag : strings.soldOut;
        [addBtn, stickyBtn].forEach(function (b) { if (b) b.disabled = !available; });
        if (addLabel) addLabel.textContent = label;
        if (stickyBtn) stickyBtn.textContent = label;
      }

      function scrollToMedia(variant) {
        if (!variant || !variant.featured_media) return;
        var slide = $('[data-media-id="' + variant.featured_media.id + '"]', section);
        var track = $('[data-gallery-track]', section);
        if (!slide || !track) return;
        if (window.matchMedia('(min-width: 990px)').matches) {
          if (slide.previousElementSibling) track.insertBefore(slide, track.firstElementChild);
          track.firstElementChild.classList.add('pdp__slide--lead');
          $$('.pdp__slide', track).forEach(function (s, i) { s.classList.toggle('pdp__slide--lead', i === 0); });
        } else {
          track.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
        }
      }

      var renderController;
      function renderVariant(variant) {
        if (renderController) renderController.abort();
        renderController = 'AbortController' in window ? new AbortController() : null;
        var url = section.getAttribute('data-product-url') + '?variant=' + variant.id + '&section_id=' + sectionId;
        fetch(url, renderController ? { signal: renderController.signal } : {})
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = parseHTML(html);
            $$('[data-update]', section).forEach(function (el) {
              var key = el.getAttribute('data-update');
              var fresh = $('[data-update="' + key + '"]', doc);
              if (fresh) el.innerHTML = fresh.innerHTML;
            });
          })
          .catch(function () {});
      }

      function onChange() {
        var opts = selectedOptions();
        $$('[data-option-value]', section).forEach(function (el) {
          el.textContent = opts[parseInt(el.getAttribute('data-option-value'), 10)] || '';
        });
        markAvailability(opts);
        var variant = product.variants.find(function (v) {
          return v.options.every(function (o, i) { return o === opts[i]; });
        });
        setButton(variant);
        if (!variant) return;
        if (idInput) { idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
        if (window.history.replaceState) {
          var u = new URL(window.location.href);
          u.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', u.toString());
        }
        scrollToMedia(variant);
        renderVariant(variant);
      }

      inputs.forEach(function (el) { el.addEventListener('change', onChange); });
      if (inputs.length) markAvailability(selectedOptions());

      /* Add to bag */
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          if (!addBtn || addBtn.disabled) return;
          addBtn.classList.add('is-loading');
          addBtn.setAttribute('aria-busy', 'true');
          addToCart(new FormData(form), errorEl).finally(function () {
            addBtn.classList.remove('is-loading');
            addBtn.removeAttribute('aria-busy');
          });
        });
      }

      /* Mobile gallery counter */
      var track = $('[data-gallery-track]', section);
      var counter = $('[data-gallery-index]', section);
      var dots = $$('.pdp__dot', section);
      if (track) {
        track.addEventListener('scroll', debounce(function () {
          var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (counter) counter.textContent = i + 1;
          dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
        }, 50), { passive: true });
      }

      /* Sticky add-to-bag */
      var sticky = $('[data-sticky-atc]', section);
      if (sticky && addBtn && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          var e = entries[0];
          var show = !e.isIntersecting && e.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
        }).observe(addBtn);
      }
    });
  }

  /* ---------------------------------------------------------------------
     Product recommendations
     --------------------------------------------------------------------- */
  function initRecommendations(root) {
    $$('[data-recommendations]', root).forEach(function (el) {
      if (el.dataset.loaded) return;
      el.dataset.loaded = 'true';
      fetch(el.getAttribute('data-url'))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var fresh = $('[data-recommendations]', parseHTML(html));
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
            initSliders(el);
            initReveal(el);
          } else {
            var wrapper = el.closest('.shopify-section');
            if (wrapper) wrapper.hidden = true;
          }
        })
        .catch(function () {});
    });
  }

  /* ---------------------------------------------------------------------
     Collection filters & sort
     --------------------------------------------------------------------- */
  function initFacets(root) {
    $$('[data-sort]', root).forEach(function (select) {
      select.addEventListener('change', function () {
        var form = document.getElementById(select.getAttribute('form'));
        if (form) { cleanFacetForm(form); form.submit(); }
      });
    });
    $$('[data-facets-form]', root).forEach(function (form) {
      form.addEventListener('submit', function () { cleanFacetForm(form); });
    });
  }
  function cleanFacetForm(form) {
    $$('input[type="number"]', form).forEach(function (input) { if (input.value === '') input.disabled = true; });
  }

  /* ---------------------------------------------------------------------
     Predictive search
     --------------------------------------------------------------------- */
  var searchModal = $('[data-search-modal]');
  function openSearch(opener) {
    if (!searchModal) { window.location.href = '/search'; return; }
    openLayer(searchModal, opener);
    var input = $('[data-predictive-input]', searchModal);
    if (input) setTimeout(function () { input.focus(); }, 80);
  }
  function initPredictiveSearch() {
    if (!searchModal) return;
    var input = $('[data-predictive-input]', searchModal);
    var results = $('[data-predictive-results]', searchModal);
    var controller;
    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      var url = routes.predictiveSearch + '?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search';
      fetch(url, controller ? { signal: controller.signal } : {})
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var fresh = $('#predictive-search-results', parseHTML(html));
          results.innerHTML = fresh ? fresh.outerHTML : '';
          input.setAttribute('aria-expanded', fresh ? 'true' : 'false');
        })
        .catch(function () {});
    }, 250);
    input.addEventListener('input', run);
  }

  /* ---------------------------------------------------------------------
     Newsletter popup
     --------------------------------------------------------------------- */
  var POPUP_KEY = 'veloura_popup_dismissed';
  function rememberPopup() {
    try { localStorage.setItem(POPUP_KEY, String(Date.now())); } catch (e) {}
  }
  function initPopup() {
    var popup = $('[data-popup]');
    if (!popup || (window.Shopify && window.Shopify.designMode)) return;
    var submitted = false;
    try { submitted = sessionStorage.getItem('veloura_popup_submitted') === '1'; } catch (e) {}
    var posted = /customer_posted=true/.test(window.location.search) || /PopupNewsletter/.test(window.location.hash);
    if (submitted && (posted || $('[data-popup-success]', popup) || $('.form-message--error', popup))) {
      try { sessionStorage.removeItem('veloura_popup_submitted'); } catch (e) {}
      openLayer(popup, null);
      return;
    }
    if (theme.popupFrequency !== 'home_every') {
      var dismissed = 0;
      try { dismissed = parseInt(localStorage.getItem(POPUP_KEY), 10) || 0; } catch (e) {}
      if (dismissed && Date.now() - dismissed < 7 * 24 * 60 * 60 * 1000) return;
    }
    var form = $('form', popup);
    if (form) form.addEventListener('submit', function () {
      rememberPopup();
      try { sessionStorage.setItem('veloura_popup_submitted', '1'); } catch (e) {}
    });
    setTimeout(function () {
      if (!active && !document.hidden) openLayer(popup, null);
      else setTimeout(function () { if (!active) openLayer(popup, null); }, 6000);
    }, typeof theme.popupDelay === 'number' ? theme.popupDelay : 3000);
  }

  /* ---------------------------------------------------------------------
     Footer accordions (mobile)
     --------------------------------------------------------------------- */
  function initFooter() {
    var mq = window.matchMedia('(max-width: 989px)');
    function sync() { $$('.footer__details').forEach(function (d) { d.open = !mq.matches; }); }
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
  }

  /* ---------------------------------------------------------------------
     Video hero: rotating clips, progress bars, rotating brand names
     --------------------------------------------------------------------- */
  function initVideoHero(root) {
    $$('[data-video-hero]', root).forEach(function (hero) {
      var clips = $$('[data-clip]', hero);
      var bars = $$('[data-clip-to]', hero);
      var title = $('[data-clip-title]', hero);
      var fallbackSpeed = parseInt(hero.getAttribute('data-speed'), 10) || 7000;
      var FADE = 0.9; // seconds before the end of a clip to start the crossfade
      var index = 0, timer = null, inView = true, switching = false, manualStop = false;

      function video(clip) { return clip ? clip.querySelector('video') : null; }
      function setBar(seconds) {
        hero.style.setProperty('--speed', Math.max(1, seconds) + 's');
        bars.forEach(function (bar, k) {
          bar.classList.remove('is-active');
          if (k === index) { void bar.offsetWidth; bar.classList.add('is-active'); }
        });
      }
      function playVideo(v) {
        if (!v || reducedMotion || !inView) return;
        v.muted = true;
        v.playsInline = true;
        if (v.preload === 'none') v.preload = 'auto';
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }
      function next() {
        if (switching || clips.length < 2 || manualStop) return;
        switching = true;
        go(index + 1);
      }
      function go(n) {
        clearTimeout(timer);
        index = (n + clips.length) % clips.length;
        clips.forEach(function (clip, k) {
          var on = k === index;
          clip.classList.toggle('is-active', on);
          var v = video(clip);
          if (!v) return;
          if (on) {
            v.muted = true;
            v.playsInline = true;
            v.setAttribute('disablePictureInPicture', '');
            try { v.currentTime = 0; } catch (e) {}
            playVideo(v);
          } else {
            setTimeout(function () { if (!clip.classList.contains('is-active')) v.pause(); }, 1300);
          }
        });
        var upcoming = video(clips[(index + 1) % clips.length]);
        if (upcoming && upcoming.preload === 'none') upcoming.preload = 'auto';
        if (title) title.textContent = clips[index].getAttribute('data-label') || '';

        var v = video(clips[index]);
        if (v && !reducedMotion) {
          if (v.duration && isFinite(v.duration)) setBar(v.duration - FADE);
          else v.addEventListener('loadedmetadata', function onMeta() {
            v.removeEventListener('loadedmetadata', onMeta);
            if (clips[index].contains(v)) setBar(v.duration - FADE);
          });
        } else {
          setBar(fallbackSpeed / 1000);
          if (!reducedMotion && clips.length > 1) timer = setTimeout(next, fallbackSpeed);
        }
        setTimeout(function () { switching = false; }, 400);
      }

      clips.forEach(function (clip, k) {
        var v = video(clip);
        if (!v || clips.length < 2) return;
        v.loop = false;
        v.addEventListener('timeupdate', function () {
          if (k !== index || !v.duration) return;
          if (v.duration - v.currentTime <= FADE) next();
        });
        v.addEventListener('ended', function () { if (k === index) next(); });
      });

      bars.forEach(function (bar) {
        bar.addEventListener('click', function () { manualStop = false; switching = false; go(parseInt(bar.getAttribute('data-clip-to'), 10)); });
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          inView = entries[0].isIntersecting;
          var v = video(clips[index]);
          if (inView) playVideo(v); else if (v) v.pause();
          hero.classList.toggle('is-paused', !inView);
        }, { threshold: 0.15 }).observe(hero);
      }
      if (reducedMotion || clips.length < 2) hero.classList.add('is-paused');

      var words = $$('[data-rotate-words] > *', hero);
      if (words.length > 1 && !reducedMotion) {
        var w = 0;
        clearInterval(hero._wordTimer);
        hero._wordTimer = setInterval(function () {
          words[w].classList.remove('is-active');
          w = (w + 1) % words.length;
          words[w].classList.add('is-active');
        }, 2200);
      }

      hero._go = go;
      hero._stop = function () { manualStop = true; clearTimeout(timer); hero.classList.add('is-paused'); };
      go(0);
    });
  }

  /* ---------------------------------------------------------------------
     Count-up numbers
     --------------------------------------------------------------------- */
  function initCounters(root) {
    var nodes = $$('[data-count-to]', root);
    if (!nodes.length) return;
    function run(el) {
      var to = parseFloat(el.getAttribute('data-count-to')) || 0;
      if (reducedMotion || to === 0) { el.textContent = to; return; }
      var start = performance.now();
      function tick(t) {
        var k = Math.min(1, (t - start) / 1600);
        el.textContent = Math.round(to * (1 - Math.pow(1 - k, 3)));
        if (k < 1) requestAnimationFrame(tick);
      }
      el.textContent = '0';
      requestAnimationFrame(tick);
    }
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.4 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---------------------------------------------------------------------
     Spotlight: scroll-linked rotation + points reveal
     --------------------------------------------------------------------- */
  function initSpotlight(root) {
    $$('[data-spotlight]', root).forEach(function (sec) {
      $$('.spotlight__video', sec).forEach(function (film) {
        film.muted = true;
        if (reducedMotion) { film.pause(); return; }
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            var visible = entries[0].isIntersecting && film.offsetParent !== null;
            if (visible) { if (film.preload === 'none') film.preload = 'auto'; var pr = film.play(); if (pr && pr.catch) pr.catch(function () {}); }
            else film.pause();
          }, { threshold: 0.2 }).observe(film);
        }
      });
      var points = $$('[data-point]', sec);
      var ticking = false;
      function update() {
        var r = sec.getBoundingClientRect();
        var p = 1 - (r.top + r.height / 2) / (window.innerHeight + r.height / 2);
        p = Math.min(1, Math.max(0, p));
        if (!reducedMotion) sec.style.setProperty('--p', p.toFixed(3));
        points.forEach(function (pt, i) { if (p > 0.25 + i * 0.1 || reducedMotion) pt.classList.add('is-on'); });
        ticking = false;
      }
      window.addEventListener('scroll', function () {
        if (!ticking) { requestAnimationFrame(update); ticking = true; }
      }, { passive: true });
      update();
    });
  }

  /* ---------------------------------------------------------------------
     Init + theme editor support
     --------------------------------------------------------------------- */
  function initSection(root) {
    initVideoHero(root);
    initCounters(root);
    initSpotlight(root);
    initAnnouncement(root);
    initSlideshow(root);
    initSliders(root);
    initQuotes(root);
    initProduct(root);
    initRecommendations(root);
    initFacets(root);
    initReveal(root);
  }

  function init() {
    initHeader();
    initPredictiveSearch();
    initFooter();
    initSection(document);
    initPopup();
    window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCount(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function (e) {
    initSection(e.target);
    if (e.target.querySelector('[data-header]')) initHeader();
    if (e.target.querySelector('.footer')) initFooter();
  });
  document.addEventListener('shopify:block:select', function (e) {
    var hero = e.target.closest('[data-video-hero]');
    if (hero && hero._go) {
      hero._stop();
      hero._go($$('[data-clip]', hero).indexOf(e.target));
      return;
    }
    var show = e.target.closest('[data-slideshow]');
    if (show && show._go) {
      show._stop();
      show._go($$('[data-slide]', show).indexOf(e.target));
    }
  });

  window.VelouraTheme = { addToCart: addToCart, openCart: function () { openLayer($('[data-cart-drawer]')); } };
})();
